import { useEffect, useState } from "react";
import { flexRender } from "@tanstack/react-table";

import Debug from "@/react/debug";

// TODO: Tidy up as part of TAP-2809
export const useTableFilters = (columns, setColumnFilters, setColumnDef) => {
  const [displayFilters, setDisplayFilters] = useState(() => {
    const initialFilters = new Map();
    columns.forEach((column) => {
      if (column.columnDef.defaultDisplayFilter) {
        initialFilters.set(column.id, {
          column,
          ...column.columnDef.defaultDisplayFilter,
          shouldOpen: false,
        });
      }
    });
    return initialFilters;
  });

  // Sync displayFilters with actual columnFilters in the table,
  // column definition is changed to include the filterFn
  useEffect(() => {
    if (displayFilters.size === 0) {
      setColumnFilters([]);
      return;
    }

    const displayFiltersArray = Array.from(displayFilters.values());

    if (displayFiltersArray.some(shouldSkipFilter)) {
      return;
    }

    setColumnFilters(
      displayFiltersArray.map((displayFilter) => ({
        id: displayFilter.column.id,
        value: displayFilter.value,
      }))
    );

    setColumnDef((prevColumnDef) =>
      prevColumnDef.map((columnDef) => {
        if (!displayFilters.has(columnDef.id)) {
          return columnDef;
        }

        const displayFilterFn = getFilterFn(displayFilters.get(columnDef.id));
        if (!displayFilterFn) {
          throw new Error(
            `Invalid filter function for column "${columnDef.id}"`
          );
        }
        columnDef.filterFn = displayFilterFn.filterFn;
        return columnDef;
      })
    );
  }, [displayFilters, setColumnFilters]);

  const setFilterValue = (filter) => (value) => {
    const newFilters = new Map(displayFilters);
    newFilters.set(filter.column.id, { ...filter, value });
    setDisplayFilters(newFilters);
  };

  const setFilterFnId = (filter, filterFnId) => {
    const newFilters = new Map(displayFilters);
    newFilters.set(filter.column.id, { ...filter, filterFnId });
    setDisplayFilters(newFilters);
  };

  const removeFilter = (filter) => {
    const newFilters = new Map(displayFilters);
    newFilters.delete(filter.column.id);
    setDisplayFilters(newFilters);
  };

  const addDisplayFilter = (column) => {
    setDisplayFilters((prevFilters) => {
      const newFilters = new Map(prevFilters);
      // If the filter already exists, open it
      if (newFilters.has(column.id)) {
        newFilters.set(column.id, {
          ...newFilters.get(column.id),
          shouldOpen: true,
        });
        return newFilters;
      }

      newFilters.set(column.id, {
        column,
        filterFnId: getFilterFns({ column }).keys().next().value,
        value: null,
        shouldOpen: true,
      });
      return newFilters;
    });
  };

  const addPresetFilter = (column, filterFnId, value, replace = false) => {
    Debug.log("[useTableFilters] [addPresetFilter] called with params.", {
      column,
      filterFnId,
      value,
      replace,
    });

    setDisplayFilters((currentFilters) => {
      const nextFilters = new Map(currentFilters);

      if (!getFilterFns({ column }).has(filterFnId)) {
        throw new Error(
          `addPresetFilter is trying to apply filterFn '${filterFnId}' which is not supported on column '${column.id}'.`
        );
      }

      if (!replace && currentFilters.has(column.id)) {
        Debug.warn(
          `[addPresetFilter] A filter already exists on column '${column.id}'. Use replace to update.`
        );
        return currentFilters;
      }

      nextFilters.set(column.id, {
        column,
        filterFnId,
        value,
        shouldOpen: false,
      });

      return nextFilters;
    });
  };

  return {
    displayFilters,
    setFilterValue,
    setFilterFnId,
    addDisplayFilter,
    removeFilter,
    addPresetFilter,
  };
};

/** @return Map */
export const getFilterFns = (displayFilter) =>
  displayFilter.column.columnDef.filterFns || new Map();

export const getFilterFn = (displayFilter) =>
  getFilterFns(displayFilter).get(displayFilter.filterFnId);

export const renderFilterHeader = (table, displayFilter) =>
  flexRender(displayFilter.column.columnDef.header, {
    table,
    column: displayFilter.column,
  });

const shouldSkipFilter = (displayFilter) =>
  getFilterFn(displayFilter).filterComponent &&
  (displayFilter.value === undefined ||
    displayFilter.value === null ||
    displayFilter.value === "");
